import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Trash2, Download, RefreshCw } from 'lucide-react';
import {
  deleteSummary,
  listSummaries,
} from '../../services/summaries.service.ts';
import type { StoredMeetingSummary } from '../meeting/types/minutes.types.ts';

function SummariesPage() {
  const [items, setItems] = useState<StoredMeetingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<StoredMeetingSummary | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listSummaries();
      setItems(list);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu tutanağı silmek istediğinize emin misiniz?')) return;
    await deleteSummary(id);
    if (selected?.id === id) setSelected(null);
    await refresh();
  };

  const handleDownload = (summary: StoredMeetingSummary) => {
    const blob = new Blob([summary.bodyMarkdown], {
      type: 'text/markdown;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tutanak_${summary.meetingCode}_${summary.endedAt}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="text-white max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-7 h-7 text-purple-400" />
            Toplantı Özetlerim
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Tutanaklar cihazınızda saklanır. Konuşma dökümü self-hosted Whisper ile üretilir;
            OpenAI’ye gönderilmez.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Yenile
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-center py-12">Yükleniyor…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
          <p className="text-gray-300 mb-2">Henüz tutanak yok.</p>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            Toplantıda host olarak “Tutanak / otomatik döküm”ü açın. Konuşmalar arka planda
            yazıya dökülür; ayrılırken tutanak otomatik kaydedilir.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-6">
          <ul className="lg:col-span-2 space-y-2">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => setSelected(item)}
                  className={`w-full text-left p-4 rounded-xl border transition ${
                    selected?.id === item.id
                      ? 'bg-purple-600/20 border-purple-500/50'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  <p className="font-medium truncate">{item.title}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(item.endedAt).toLocaleString('tr-TR')} · {item.durationSeconds} sn
                    · {item.transcript.length} satır döküm
                  </p>
                </button>
              </li>
            ))}
          </ul>

          <div className="lg:col-span-3 bg-white/5 border border-white/10 rounded-2xl p-5 min-h-[320px]">
            {selected ? (
              <>
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => handleDownload(selected)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Markdown indir
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(selected.id)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600/80 hover:bg-red-600 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Sil
                  </button>
                </div>
                <pre className="whitespace-pre-wrap text-sm text-gray-200 font-sans leading-relaxed max-h-[70vh] overflow-y-auto">
                  {selected.bodyMarkdown}
                </pre>
              </>
            ) : (
              <p className="text-gray-500 text-center py-20">Detay için bir tutanak seçin.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SummariesPage;
